import { TemplatePlaceholder } from '@/types';
import { ElectorateRecordResult } from '@/types/filterCriteriaRecords';
import { AgDialog } from '@australiangreens/ag-internal-components';
import ToggleDebugButton from '@components/ToggleDebugButton';
import { Box, FormControlLabel, FormHelperText, Grid, Switch, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import TemplateCapableFetchAutocomplete from '@/components/TemplateCapableFetchAutocomplete';

import { unflattenSearchTree } from '@components/LoadSearchTemplateDialog/flatSearchTree';
import MappedTemplateFormControl from '@components/LoadSearchTemplateDialog/MappedTemplateFormControl';

import type { FilterFieldChangePayload } from './types';
import { fieldHash } from './types';
import { useLoadSearchTemplateDialog } from './useLoadSearchTemplateDialog';

type LoadSearchTemplateDialogLayoutProps = {
  dialog: ReturnType<typeof useLoadSearchTemplateDialog>;
};

export default function LoadSearchTemplateDialogLayout({ dialog }: LoadSearchTemplateDialogLayoutProps) {
  const {
    searchTemplate,
    enableDevFeatures,
    debug,
    setDebug,
    overrideValidation,
    setOverrideApply,
    placeholders,
    initialFlatSearchTemplateTree,
    metaDataOfPlaceholderFields,
    flatSearchTree,
    initialEntitiesByPlaceholderKey,
    electionElectorateRecords,
    setElectorateOverrideMap,
    handleChange,
    allPlaceholderFieldsValid,
    isValidEventAttendance,
    numEventAttendance,
    handleLoadSearchTemplateApply,
    handleClose,
  } = dialog;

  const dialogTitle = enableDevFeatures ? (
    <Box sx={{ '& > p': { margin: '0px' } }}>
      <p>
        Specify details for your search
        <ToggleDebugButton debug={debug} setDebug={setDebug} sx={{ verticalAlign: 'middle' }} />
        <FormControlLabel
          label="Override validation"
          sx={{ marginLeft: '16px' }}
          control={
            <Switch
              checked={overrideValidation}
              onChange={(event) => setOverrideApply(event.target.checked)}
            />
          }
        />
      </p>
    </Box>
  ) : (
    'Specify details for your search'
  );

  return (
    <AgDialog
      dialogTitle={dialogTitle}
      isOpen={searchTemplate !== undefined}
      onClose={handleClose}
      primaryButton={{
        text: 'Apply',
        disabled:
          (!allPlaceholderFieldsValid && !overrideValidation) ||
          (numEventAttendance > 0 && !isValidEventAttendance),
        onClick: handleLoadSearchTemplateApply,
      }}
      maxWidth={debug ? 'xl' : 'sm'}
    >
      <Grid container spacing={2}>
        <Grid
          size={debug ? 6 : 12}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem"
          }}>
          <Typography variant="body1" data-testid="intro">
            Using the template <strong>{searchTemplate?.name}</strong>:{' '}
            {searchTemplate?.description}
          </Typography>

          <Typography variant="body1">
            <em>Fill in the below fields to proceed</em>
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {placeholders.map((templatePlaceholder, idx) => {
              const { nodeId, filterNameAndField, placeholder } = templatePlaceholder;
              const metaData = metaDataOfPlaceholderFields[fieldHash(nodeId, filterNameAndField)];
              const initialFlatSearchTreeNodeValue = initialFlatSearchTemplateTree.nodes[nodeId];
              return (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    paddingTop: "16px",
                    paddingBottom: "16px",
                    gap: "8px"
                  }}>
                  <Typography variant="body1">{placeholder.description}</Typography>

                  {filterNameAndField !== 'roster.electorateIds' && (
                    <MappedTemplateFormControl
                      filterNameAndField={filterNameAndField}
                      onChange={(value, fieldMetaData) => {
                        handleChange(nodeId, {
                          filterNameAndField,
                          value,
                          fieldMetaData,
                        } as FilterFieldChangePayload);
                      }}
                      fieldError={metaData.error}
                      initialFlatSearchTreeNode={initialFlatSearchTreeNodeValue}
                      initialValue={
                        initialEntitiesByPlaceholderKey[fieldHash(nodeId, filterNameAndField)]
                      }
                    />
                  )}

                  {filterNameAndField === 'roster.electorateIds' && (
                    <>
                      <TemplateCapableFetchAutocomplete
                        key={
                          !electionElectorateRecords[nodeId]
                            ? 0
                            : electionElectorateRecords[nodeId]!.electionId
                        }
                        preLoadedOptions={
                          !electionElectorateRecords[nodeId]
                            ? []
                            : electionElectorateRecords[nodeId]!.electorates
                        }
                        minLength={0}
                        label="Specify electorate(s)"
                        value={
                          !electionElectorateRecords[nodeId]
                            ? []
                            : electionElectorateRecords[nodeId]!.electorateValue
                        }
                        onChange={(newValue: ElectorateRecordResult[] | TemplatePlaceholder) => {
                          const ourRealNewValue = newValue as unknown as ElectorateRecordResult[];
                          setElectorateOverrideMap((prev) => ({
                            ...prev,
                            [nodeId]: {
                              electionId: electionElectorateRecords[nodeId]!.electionId,
                              electorates: electionElectorateRecords[nodeId]!.electorates,
                              electorateValue: ourRealNewValue,
                            },
                          }));

                          const state = ourRealNewValue.length > 0 ? 'valid' : 'empty';

                          handleChange(nodeId, {
                            filterNameAndField,
                            value: ourRealNewValue.map((x) => x.id),
                            fieldMetaData: { state, error: null },
                          } as unknown as FilterFieldChangePayload);
                        }}
                        data-testid="getelectorates"
                        loadingText="Looking for electorates..."
                        noOptionsText="No electorates found"
                        isOnTemplatePage={true}
                        templateBuilderDisplayName="Roster filter: Electorates"
                        textFieldColor="secondary"
                      />
                      {!electionElectorateRecords[nodeId] && (
                        <FormHelperText sx={{ color: 'black !important' }}>
                          {'Please select an Election'}
                        </FormHelperText>
                      )}
                    </>
                  )}
                </Box>
              );
            })}
          </LocalizationProvider>
        </Grid>
        {debug && (
          <Grid size={6} sx={{ fontSize: '10px' }}>
            <pre>
              {flatSearchTree
                ? JSON.stringify(unflattenSearchTree(flatSearchTree), null, 2)
                : 'Undefined'}
            </pre>
          </Grid>
        )}
      </Grid>
    </AgDialog>
  );
}
